import * as React from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import styled from "styled-components";
import { Button, Col, Row } from "react-bootstrap";
import {Routine} from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";
import TableExport from "../../Elements/Export/TableExport";
import { getExportData, ExportType } from "./Log/utils";
import { Device, Transaction } from "./Store/types";
import { DeviceHardwareEnum } from "../DeviceHardwareEnum";
import { AlarmFilterEnum } from "../DeviceList/AlarmFilterEnum";
import { DisplayGraph, displayGraph, FilterAlarm, filterAlarm, SelectDate, selectDate } from "./Store/actions";
import SmallPin from "../../Elements/Icon/Icons/SmallPin";
import { getCurrentPosition } from "./Store/routines";
import  Loader  from "@Elements/Loader";
import { AllPermissions, PolicyContext } from "../Policy/Provider";
import { PolicyNameEnum } from "../PolicyNameEnum";
import { AppState } from "@Safemate/Store/types";
import Analysis from "@Elements/Icon/Icons/FontAwesome/Analysis";
import { LogParams, MapType } from ".";
import { useParams } from "react-router";
import { SvgButton } from "@Safemate/styles";
import Filter from "./Filter";
import { IDevice } from "@Safemate/Model/Device";
import {Capabilities} from "@Safemate/Settings/Store/types";
import { ManualTracking } from "@Safemate/Settings/Tracking/trackingInfo";
import GetPosition from "@Safemate/Settings/Tracking/TrackingMode/getPosition";
import FindMe from "@Safemate/Settings/Tracking/TrackingMode/findMe";

interface HeaderProps{
  filteredTransactions: Transaction[];
  serialNumber: string;
  dehaId: number;
  alarmFilter: AlarmFilterEnum[];
  filterAlarm: FilterAlarm;
  selectDate: SelectDate;
  getCurrentPosition: Routine<ActionFunctionAny<Action<any>>>;
  fetchingPosition: boolean;
  datesWithTransactions: number[];
  safemateAsReceiver: boolean;
  minionTwin: boolean;
  isTrackerOnly: boolean;
  displayGraph: DisplayGraph;
  mapType: MapType;
  devices: IDevice[];
  googleMaps: any;
  device: Device;
  gx8: boolean;
  loadingBatteryData: boolean;
  loadingSignalData: boolean;
  capabilities: Map<number, Capabilities>;
}

const mapStateToProps = ({ appData:{ isTrackerOnly, capabilities}, transactionLog: { transaction: { loadingBatteryData, loadingSignalData, filteredTransactions, alarmFilter, datesWithTransactions, googleMaps }, device: { connected, serialNumber, dehaId, safemateAsReceiver, minionTwin, devices, gx8 }, status: { fetchingPosition }}}: AppState) => {
  return{
    filteredTransactions,
    serialNumber,
    dehaId,
    alarmFilter,
    fetchingPosition,
    datesWithTransactions,
    safemateAsReceiver,
    minionTwin,
    isTrackerOnly,
    devices,
    googleMaps,
    gx8,
    loadingBatteryData,
    loadingSignalData,
    capabilities,
    connected
  }
}

const mapDispatchToProps = {
  filterAlarm,
  selectDate,
  getCurrentPosition,
  displayGraph
}

const Header = connect(mapStateToProps, mapDispatchToProps)(({
  displayGraph,
  isTrackerOnly,
  mapType,
  googleMaps,
  gx8,
  loadingBatteryData,
  loadingSignalData,
                                                               capabilities,
}: HeaderProps) => {

  const { formatMessage } = useIntl();

  
  const permissions = React.useContext(PolicyContext) as Map<PolicyNameEnum, AllPermissions>;
  const logPermission = permissions.get(PolicyNameEnum.DEVICE_TRANSACTION_LOG).generalPermissions.all

  const loadingGraphData = loadingBatteryData || loadingSignalData;
  
  if(isTrackerOnly || !logPermission) return null;
  return(
    <>
      <Row style={{display: "flex", alignItems: "end"}}>
        <Col style={{display: "flex"}} md={10}>
          <Filter mapType={mapType}/>
        </Col>
        <Col style={{display: "flex", justifyContent: "end"}} md={2}>
          <SvgButton style={{marginLeft: "10px"}} onClick={!loadingGraphData && displayGraph}>
            {loadingGraphData 
              ? <Loader/> 
              : <><Analysis medium/>{formatMessage({id: "graph", defaultMessage: "Graf"})}</>}
            </SvgButton>
        </Col>
      </Row>
    </>
  )
})

export default Header;

interface ActionButtonsProps{
  filteredTransactions: Transaction[];
  serialNumber: string;
  dehaId: number;
  alarmFilter: AlarmFilterEnum[];
  filterAlarm: FilterAlarm;
  selectDate: SelectDate;
  getCurrentPosition: Routine<ActionFunctionAny<Action<any>>>;
  fetchingPosition: boolean;
  datesWithTransactions: number[];
  safemateAsReceiver: boolean;
  minionTwin: boolean;
  isTrackerOnly: boolean;
  connected: boolean;
  devices: IDevice[];
  mapType: MapType;
}

export const ActionButtons = connect(mapStateToProps, mapDispatchToProps)(({ filteredTransactions, connected, dehaId, mapType, devices, getCurrentPosition, fetchingPosition, serialNumber  }: ActionButtonsProps) => {

  const { id } = useParams<LogParams>();
  const deviceId = parseInt(id);

  const [ csvData, setCsvData ] = React.useState<string[][]>([]);
  const [ pdfData, setPdfData ] = React.useState<string[][]>([]);
  
  const permissions = React.useContext(PolicyContext) as Map<PolicyNameEnum, AllPermissions>;

  const supportFetchPosition = dehaId === DeviceHardwareEnum.WATCH_ONE || dehaId === DeviceHardwareEnum.WATCH_TWO || dehaId === DeviceHardwareEnum.WATCH_THREE || dehaId === DeviceHardwareEnum.WATCH_FIVE || dehaId === DeviceHardwareEnum.WATCH_SEVEN || dehaId === DeviceHardwareEnum.TRIGGER_THREE_E || dehaId === DeviceHardwareEnum.TRIGGER_FOUR || dehaId === DeviceHardwareEnum.TRIGGER_THREE || dehaId === DeviceHardwareEnum.TRIGGER_TWO_4G || dehaId === DeviceHardwareEnum.WATCH_FOUR || dehaId === DeviceHardwareEnum.TRACK_TWO;

  const { formatMessage } = useIntl();

  let csvTimeout: NodeJS.Timeout;
  let pdfTimeout: NodeJS.Timeout;
  
  const name = `${formatMessage({id: "transactions", defaultMessage: "Transactions"})}_${serialNumber}`;

  React.useEffect(() => {
    csvTimeout = setTimeout(() => {
      setCsvData(getExportData(ExportType.CSV, filteredTransactions, formatMessage, dehaId, mapType, filteredTransactions, devices));
    }, 1000);
    pdfTimeout = setTimeout(() => {
      setPdfData(getExportData(ExportType.PDF, filteredTransactions, formatMessage, dehaId, mapType, filteredTransactions, devices));
    }, 1000);
    return () => {
      // Cleanup polling
      clearTimeout(csvTimeout);
      clearTimeout(pdfTimeout);
    }
  }, [filteredTransactions])

  const getPos = () => {
    if(!fetchingPosition)
    getCurrentPosition(deviceId)
  }

  return(
    <>
      <ManualTracking /><GetPosition /><FindMe deviId={deviceId}/>
      {permissions.get(PolicyNameEnum.DEVICE_LOGS).generalPermissions.all && mapType === MapType.ALL &&
      <React.Fragment>
        <TableExport type="csv" data={csvData} fileName={name} />
        <TableExport colWidth={[10, 14, 18, 10, 22, 8, 8, 10]} type="pdf" data={pdfData} fileName={name} />
      </React.Fragment>}
    </>
  )
})
